import {useCallback, useEffect, useMemo, useRef, useState, type ReactNode} from 'react'
import {SelectPanel, SkeletonBox} from '@primer/react'
import {CommentDiscussionIcon, PlusIcon} from '@primer/octicons-react'
import {ReposSelector} from '@github-ui/repos-selector'
import {scopedRepositoryQuery} from '@github-ui/agent-sessions-interface/components/repo-picker/scope'
import {sendEvent} from '@github-ui/hydro-analytics'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {buildNewRepositoryUrl, queryForRepos} from '../task-chat-input-toolbar/TaskChatInputToolbar'
import type {AgentDropdownRepository} from '@github-ui/copilot-chat-types'
import type {TaskComposerTargetSelection} from '@github-ui/copilot-shared-ui/utils/task-composer-target-selection'

import {EnvironmentPicker} from './EnvironmentPicker'
import {CLOUD_ENVIRONMENT_ID, type EnvironmentOption} from './environment-option'
import {RepoBranchPill} from './RepoBranchPill'
import {isRepoInScope, repoPickerScopeSubtitle} from '../shared'
import styles from './EnvironmentStrip.module.css'

export interface EnvironmentStripProps {
  hideRepoSelector?: boolean
  initialRepoNWO?: string
  initialBranch?: string
  targetSelection: TaskComposerTargetSelection
  onTargetSelectionChange: (selection: TaskComposerTargetSelection) => void
  selectedBranch: string | undefined
  onBranchSelect: (branch: string | undefined) => void
  currentPrompt?: string
  /** Login of the current user, used to pre-fill the owner on the new repository URL. */
  currentUserLogin?: string
  selectedEnvironment: EnvironmentOption | null
  /** Effective environment when a launcher supplies a destination outside the stored selection. */
  selectedOption?: EnvironmentOption | null
  design?: 'default' | 'simplified'
  onSelectEnvironment: (env: EnvironmentOption) => void
  showEnvironmentPicker?: boolean
  /** Forwarded to the environment picker; adds the managed-sandbox option. */
  includeSandboxOption?: boolean
  /**
   * Replaces the picker's default option list. A host that can start a session
   * in only some environments passes just those, so the picker never offers a
   * destination the host has no submit for.
   */
  environmentOptions?: EnvironmentOption[]
  /** Enables the Sandbox-only repo-less Chat mode for callers that support it. */
  allowChat?: boolean
  branchSelectorDisabled?: boolean
  disabled?: boolean
  scopePicker?: ReactNode
  /**
   * Filters the repo picker strictly by owner. Null blocks repository loading
   * until the scoped owner resolves; undefined preserves unscoped environments.
   */
  scopeOwner?: string | null
}

export function EnvironmentStrip({
  hideRepoSelector,
  initialRepoNWO,
  initialBranch,
  targetSelection,
  onTargetSelectionChange,
  selectedBranch,
  onBranchSelect,
  currentPrompt,
  currentUserLogin,
  selectedEnvironment,
  selectedOption,
  design = 'default',
  onSelectEnvironment,
  showEnvironmentPicker = false,
  includeSandboxOption = false,
  environmentOptions,
  allowChat = false,
  branchSelectorDisabled = false,
  disabled = false,
  scopePicker,
  scopeOwner,
}: EnvironmentStripProps) {
  const hasAutoSelectedRef = useRef(false)
  const selectedRepo = targetSelection.kind === 'repository' ? targetSelection.repository : undefined
  const canSelectChat = allowChat && selectedEnvironment?.kind === 'sandbox'
  // While the user is searching the repo picker, the "Chat" select-all option and
  // the owner subtitle are hidden so the result list reads as a flat repo list.
  // The search value only reaches this component through `repositoryLoader`, so we
  // track it there rather than reaching into ReposSelector's internal filter state.
  const [isSearching, setIsSearching] = useState(false)

  const handleTargetSelection = useCallback(
    (selection: TaskComposerTargetSelection) => {
      if (selection.kind === 'repository' && !isRepoInScope(selection.repository, scopeOwner)) return
      hasAutoSelectedRef.current = true
      onTargetSelectionChange(selection)
      if (selection.kind === 'chat') {
        onBranchSelect(undefined)
      }
    },
    [onBranchSelect, onTargetSelectionChange, scopeOwner],
  )

  // Drop the target when the scope excludes it, falling back to Chat if allowed.
  useEffect(() => {
    if (isRepoInScope(selectedRepo, scopeOwner)) return
    handleTargetSelection(canSelectChat ? {kind: 'chat'} : {kind: 'unselected'})
    onBranchSelect(undefined)
  }, [scopeOwner, selectedRepo, canSelectChat, handleTargetSelection, onBranchSelect])

  const onLoad = useCallback(
    (loadedRepositories: AgentDropdownRepository[]) => {
      const repositories = loadedRepositories.filter(repo => isRepoInScope(repo, scopeOwner))
      if (targetSelection.kind === 'unselected' && !hasAutoSelectedRef.current) {
        let initialRepo: AgentDropdownRepository | undefined
        if (initialRepoNWO) {
          initialRepo = repositories.find(repo => repo.nameWithOwner === initialRepoNWO)
          if (initialRepo && initialRepo.enabled) {
            onTargetSelectionChange({kind: 'repository', repository: initialRepo})
            onBranchSelect(initialBranch || initialRepo.defaultBranch)
            hasAutoSelectedRef.current = true
            return
          }
          // The exact repository resolver owns explicit URL and host targets.
          // Do not let this picker's partial first page overwrite that selection.
          return
        }
        const lastUsedRepo = copilotLocalStorage.getLastUsedRepository()
        if (lastUsedRepo) {
          const loadedLastUsedRepo = repositories.find(
            repo => repo.nameWithOwner === `${lastUsedRepo.ownerLogin}/${lastUsedRepo.name}`,
          )
          if (loadedLastUsedRepo && loadedLastUsedRepo.enabled) {
            onTargetSelectionChange({kind: 'repository', repository: loadedLastUsedRepo})
            onBranchSelect(loadedLastUsedRepo?.defaultBranch)
            hasAutoSelectedRef.current = true
            return
          }
        }
        const firstEnabledRepo = repositories.find(repo => repo.enabled)
        if (firstEnabledRepo) {
          onTargetSelectionChange({kind: 'repository', repository: firstEnabledRepo})
          onBranchSelect(firstEnabledRepo.defaultBranch)
          hasAutoSelectedRef.current = true
        }
      }
    },
    [initialRepoNWO, initialBranch, onBranchSelect, onTargetSelectionChange, targetSelection.kind, scopeOwner],
  )

  const repositoryLoader = useCallback(
    async (query: string, defaultOwner?: string) => {
      if (scopeOwner === null || scopeOwner === '') return []
      setIsSearching(query.trim().length > 0)
      const effectiveOwner = scopeOwner ?? defaultOwner
      const scopedQuery = scopedRepositoryQuery(query, scopeOwner)
      const repos = await queryForRepos(scopedQuery, effectiveOwner, initialRepoNWO)
      if (!scopeOwner) return repos
      return repos.filter(repo => isRepoInScope(repo, scopeOwner))
    },
    [initialRepoNWO, scopeOwner],
  )

  const repoPickerSubtitle = repoPickerScopeSubtitle(scopeOwner, currentUserLogin)

  const newRepoUrl = useMemo(
    () => buildNewRepositoryUrl(currentUserLogin, currentPrompt),
    [currentUserLogin, currentPrompt],
  )

  const hasPrimaryRepo = !!selectedRepo

  const newRepoLink = (
    <SelectPanel.SecondaryActionLink
      href={newRepoUrl}
      leadingVisual={PlusIcon}
      onClick={() => {
        sendEvent('agent_sessions.task_view', {target: 'NEW_REPOSITORY_LINK'})
      }}
    >
      New repository
    </SelectPanel.SecondaryActionLink>
  )

  const environmentPicker = showEnvironmentPicker ? (
    <EnvironmentPicker
      appearance="strip"
      selectedEnvironmentId={selectedEnvironment?.id ?? CLOUD_ENVIRONMENT_ID}
      selectedOption={selectedOption}
      design={design}
      isChatSelected={design === 'simplified' && targetSelection.kind === 'chat'}
      onSelectEnvironment={onSelectEnvironment}
      {...(environmentOptions ? {environments: environmentOptions} : {})}
      includeSandboxOption={includeSandboxOption}
      announceUnavailable={false}
      disabled={disabled}
    />
  ) : null

  return (
    <div className={styles.strip} data-testid="environment-strip">
      {design === 'default' && environmentPicker}

      {!hideRepoSelector && !hasPrimaryRepo && (
        <ReposSelector<AgentDropdownRepository>
          repositoryLoader={repositoryLoader}
          selectionVariant="single"
          iconOnly={false}
          responsiveStyle="full"
          selectAllOption={canSelectChat && !isSearching}
          selectAllOptionPlacement="inline"
          selectAllOptionText="Chat"
          selectAllOptionLeadingVisual={CommentDiscussionIcon}
          selectAllOptionSelected={targetSelection.kind === 'chat'}
          buttonText={targetSelection.kind === 'chat' ? 'Chat' : 'Select repository'}
          onSelect={repo => {
            if (repo) {
              handleTargetSelection({kind: 'repository', repository: repo})
              onBranchSelect(repo.defaultBranch)
            } else if (canSelectChat) {
              handleTargetSelection({kind: 'chat'})
            }
          }}
          currentSelection={selectedRepo}
          width="medium"
          visibleRowCount={6}
          additionalButtonProps={{
            'aria-label': targetSelection.kind === 'chat' ? 'Mode: Chat' : 'Select repository',
            leadingVisual: targetSelection.kind === 'chat' ? CommentDiscussionIcon : undefined,
            className: styles.stripButton,
            variant: 'invisible',
            size: 'small',
            disabled,
          }}
          onLoad={onLoad}
          loadingElement={<SkeletonBox width="120px" height="24px" />}
          secondaryAction={newRepoLink}
          subtitle={isSearching ? undefined : repoPickerSubtitle}
          restrictionOwner={scopeOwner ?? undefined}
        />
      )}

      {hasPrimaryRepo && (
        <RepoBranchPill
          pair={{id: 'primary', repo: selectedRepo, branch: selectedBranch || selectedRepo.defaultBranch}}
          onRepoChange={(_id, repo) => {
            handleTargetSelection({kind: 'repository', repository: repo})
            onBranchSelect(repo.defaultBranch)
          }}
          onBranchChange={(_id, branch) => onBranchSelect(branch)}
          onRemove={() => {
            handleTargetSelection({kind: 'unselected'})
          }}
          removable={false}
          hideRepo={hideRepoSelector}
          initialRepoNWO={initialRepoNWO}
          onLoad={onLoad}
          secondaryAction={newRepoLink}
          branchSelectorDisabled={branchSelectorDisabled}
          disabled={disabled}
          scopeOwner={scopeOwner}
          repoPickerSubtitle={repoPickerSubtitle}
          allowChat={canSelectChat}
          onChatSelect={() => handleTargetSelection({kind: 'chat'})}
        />
      )}

      {targetSelection.kind === 'chat' ? scopePicker : null}
      {design === 'simplified' && environmentPicker}
    </div>
  )
}
